import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { LabelColor } from '../../../components/label';
import Label from '../../../components/label/Label';
import Scrollbar from '../../../components/scrollbar/Scrollbar';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  getComparator,
  useTable
} from '../../../components/table';
import TableLoading from '../../../components/table/TableLoading';
import { useDynamicTableHeaders } from '../../../components/table/useDynamicTableHeaders';
import ListingOrderItemGeneral from '../../../models/owner-portal/loi';
import { TaskGeneral } from '../../../models/owner-portal/taskGeneral';
import {
  useGetRTKQListingOrderItemsQuery,
  useGetRTKQTasksQuery
} from '../../../redux/rtkQuery/apiSlice';
import {
  TASK_TAB_OPTION,
  TASK_TAB_OPTIONS,
  TASK_TYPE_OPTIONS
} from '../../../utils/owner-portal/taskConstants';
import { showTableTabCounts, TableTabInfo } from '../../../utils/owner-portal/uiConstants';
import { useTableWidth } from '../useTableWidth';
import { LoiTableRow } from './LoiTableRow';
import { TaskTableRow } from './TaskTableRow';
import TaskTableToolbar from './TaskTableToolbar';
import { Table, TableBody } from '../../../components/ui/Table';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/Tabs';
import { Separator } from '../../../components/ui/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../../../components/ui/Tooltip';
import { Button } from '../../../components/ui/Button';

interface TaskTableI {
  listingId?: string;
  filterTab?: string;
  onFilterTabChange?: (value: string) => void;
}

// ----------------------------------------------------------------------

export default function TaskTable(props: TaskTableI) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    // setSelected,
    onSelectRow,
    onSelectAllRows: _onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage
  } = useTable({
    defaultDense: true,
    defaultOrder: 'desc',
    defaultOrderBy: 'last_modified_date_ISO'
  });

  const { listingId, filterTab: filterTabProp, onFilterTabChange } = props;
  const tableMinWidth = useTableWidth();
  const singleListingMode = !!listingId;

  const [filterTabInternal, setFilterTabInternal] = useState(TASK_TAB_OPTION.loi);
  const filterTab = filterTabProp ?? filterTabInternal;
  const tabsControlled = filterTabProp !== undefined;

  useEffect(() => {
    if (tabsControlled) setPage(0);
  }, [filterTabProp, tabsControlled, setPage]);

  const {
    data: tasks,
    isSuccess: isTasksSuccess,
    isFetching: isTaskFetching
  } = useGetRTKQTasksQuery({ listingId: listingId });

  const {
    data: lois,
    isSuccess: isLOISuccess,
    isFetching: isLOIFetching
  } = useGetRTKQListingOrderItemsQuery({ listingId: listingId });

  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [_openConfirm, setOpenConfirm] = useState(false);

  const toolBarOptions = useMemo(() => {
    if (filterTab === TASK_TAB_OPTION.task) {
      return TASK_TYPE_OPTIONS;
    }
    if (isLOISuccess) {
      const subTypeOptions = Array.from(new Set(lois.map((loi) => loi.sub_type)));
      const filteredOptions = subTypeOptions.filter((option) => typeof option === 'string');
      return ['all', ...filteredOptions];
    }
    return ['all'];
  }, [filterTab, isLOISuccess, lois]);

  const TABLE_HEAD = useMemo((): {
    id: keyof TaskGeneral | keyof ListingOrderItemGeneral;
    label?: string;
    align?: 'left' | 'center' | 'right';
  }[] => {
    return filterTab === TASK_TAB_OPTION.task
      ? [
          { id: 'title', label: 'Title', align: 'left' },
          { id: 'status_mapped', label: 'Status', align: 'left' },
          { id: 'assignee_name', label: 'Assignee', align: 'left' },
          { id: 'related_name', label: 'Related To', align: 'left' },
          { id: 'dueDate', label: 'Due Date', align: 'left' },
          { id: 'last_modified_date_ISO', label: 'Updated', align: 'right' }
        ]
      : singleListingMode
      ? // LOIs
        [
          { id: 'title', label: 'Title', align: 'left' },
          { id: 'amount', label: 'Amount', align: 'left' },
          { id: 'status', label: 'Status', align: 'left' },
          { id: 'sub_type', label: 'Service', align: 'left' },
          { id: 'end_date_ISO', label: 'Date', align: 'right' }
        ]
      : [
          { id: 'name_detailed', label: 'Property', align: 'left' },
          { id: 'title', label: 'Title', align: 'left' },
          { id: 'amount', label: 'Amount', align: 'left' },
          { id: 'status', label: 'Status', align: 'left' },
          { id: 'sub_type', label: 'Service', align: 'left' },
          { id: 'end_date_ISO', label: 'Date', align: 'right' }
        ];
  }, [filterTab, singleListingMode]);

  const [dynamicTableHeaders, dynamicallyShownTableContentKeys] = useDynamicTableHeaders({
    tableHeaders: TABLE_HEAD,
    breakpoints: {
      sm:
        filterTab === TASK_TAB_OPTION.task
          ? ([
              'title',
              'related_name',
              'dueDate',
              'last_modified_date_ISO'
            ] as (keyof TaskGeneral)[])
          : ([
              'name_detailed',
              'amount',
              'title',
              'status',
              'end_date_ISO'
            ] as (keyof ListingOrderItemGeneral)[]),
      md:
        filterTab === TASK_TAB_OPTION.task
          ? ([
              'title',
              'status_mapped',
              'related_name',
              'dueDate',
              'last_modified_date_ISO'
            ] as (keyof TaskGeneral)[])
          : ([
              'name_detailed',
              'amount',
              'title',
              'status',
              'sub_type',
              'end_date_ISO'
            ] as (keyof ListingOrderItemGeneral)[])
    }
  });

  const inputData = useMemo(() => {
    const dataArr = [];

    if (isTasksSuccess && !isTaskFetching) {
      dataArr.push(...tasks);
    }

    if (isLOISuccess && !isLOIFetching) {
      dataArr.push(...lois);
    }

    return dataArr;
  }, [isTasksSuccess, isTaskFetching, isLOISuccess, isLOIFetching, tasks, lois]);

  const dataFiltered = applyFilter({
    inputData: inputData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterType,
    filterByTab: filterTab
  });

  const isFiltered = filterName !== '' || filterType !== 'all';

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterType) ||
    (!dataFiltered.length && !!filterTab);

  const handleOpenConfirm = () => {
    //REMINDER: This only pertains to _our_ confirm, not one opened by one of our table rows!
    setOpenConfirm(true);
  };

  const handleTabChange = (newValue: string) => {
    setPage(0);
    if (!tabsControlled) setFilterTabInternal(newValue as TASK_TAB_OPTION);
    onFilterTabChange?.(newValue);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleFilterType = (value: string) => {
    setPage(0);
    setFilterType(value);
  };

  const handleViewRow = async (id: string) => {
    // navigate(PATH_DASHBOARD.property.view(id));
    console.log('View row', id);
  };

  //? KEEP: But at the moment, we don't have a use case.
  const handleResetFilter = () => {
    setFilterName('');
    setFilterType('all');
    // setFilterStage(defaultStageFilter);
  };

  const _rowIds = useMemo(() => (Array.isArray(tasks) ? tasks?.map((row) => row.id) : []), [tasks]);

  // set up the table's tab definitions
  let tabInfo: null | TableTabInfo[] = null;

  if (showTableTabCounts) {
    const getLengthByModelType = (modelType: TASK_TAB_OPTION) => {
      if (!Array.isArray(inputData)) {
        return 0;
      }

      const totalTasks = inputData.filter((v) => v instanceof TaskGeneral).length;

      if (modelType === TASK_TAB_OPTION.task) {
        return totalTasks;
      }

      return inputData.length - totalTasks;
    };

    tabInfo = TASK_TAB_OPTIONS.map((v) => {
      const color = (v === TASK_TAB_OPTION.task ? 'secondary' : 'primary') as LabelColor;
      return { value: v, label: v, color: color, count: getLengthByModelType(v) };
    });
  }
  // else we'll use simple string tabs

  return (
    <>
      {!tabsControlled && (
        <>
          <Tabs value={filterTab} onValueChange={handleTabChange}>
            <TabsList variant="underline">
              {!showTableTabCounts
                ? TASK_TAB_OPTIONS.map((tab) => (
                    <TabsTrigger key={tab} value={tab} variant="underline" className="capitalize">
                      {tab}
                    </TabsTrigger>
                  ))
                : tabInfo !== null &&
                  tabInfo.map((tab, i) => (
                    <TabsTrigger
                      key={tab.value + '-' + i}
                      value={tab.value}
                      variant="underline"
                      className="capitalize"
                    >
                      <Label color={tab.color} variant="soft">
                        {tab.count}
                      </Label>
                      {tab.label}
                    </TabsTrigger>
                  ))}
            </TabsList>
          </Tabs>
          <Separator />
        </>
      )}

      <TaskTableToolbar
        label={filterTab === TASK_TAB_OPTION.task ? 'Type' : 'Service'}
        filterName={filterName}
        filterType={filterType}
        isFiltered={isFiltered}
        optionsType={toolBarOptions}
        onFilterName={handleFilterName}
        onFilterType={handleFilterType}
        onResetFilter={handleResetFilter}
      />

      <div className="relative overflow-hidden">
        <TableSelectedAction
          dense={dense}
          numSelected={selected.length}
          rowCount={dataFiltered.length}
          action={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleOpenConfirm}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />

        <Scrollbar>
          <Table className={`min-w-[${tableMinWidth}px]`}>
            <TableHeadCustom
              order={order}
              orderBy={orderBy}
              headLabel={dynamicTableHeaders}
              rowCount={dataFiltered.length}
              numSelected={selected.length}
              onSort={onSort}
            />

            <TableBody>
              {isTasksSuccess &&
                isLOISuccess &&
                dataFiltered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    if (row instanceof TaskGeneral) {
                      return (
                        <TaskTableRow
                          key={row.id}
                          row={row}
                          selected={selected.includes(row.id)}
                          onSelectRow={() => onSelectRow(row.id)}
                          onDeleteRow={() => {
                            console.warn('listing delete-single disabled');
                          }}
                          onViewRow={() => handleViewRow(row.id)}
                          showColumns={dynamicallyShownTableContentKeys as Set<keyof TaskGeneral>}
                        />
                      );
                    }
                    if (row instanceof ListingOrderItemGeneral) {
                      return (
                        <LoiTableRow
                          key={row.id}
                          singleListing={singleListingMode}
                          row={row}
                          selected={selected.includes(row.id)}
                          onSelectRow={() => onSelectRow(row.id)}
                          onDeleteRow={() => {
                            console.warn('listing delete-single disabled');
                          }}
                          onViewRow={() => handleViewRow(row.id)}
                          showColumns={
                            dynamicallyShownTableContentKeys as Set<keyof ListingOrderItemGeneral>
                          }
                        />
                      );
                    }
                    return null;
                  })}

              {/* <TableEmptyRows
                                height={denseHeight}
                                emptyRows={emptyRows(page, rowsPerPage, dataFiltered.length)}
                            /> */}

              {isTaskFetching || isLOIFetching ? (
                <TableLoading isNotFound={isNotFound} />
              ) : (
                <TableNoData
                  isNotFound={isNotFound}
                  isFiltered={isFiltered}
                  noDataText={
                    filterTab === TASK_TAB_OPTION.task ? 'No tasks found' : 'No work orders found'
                  }
                />
              )}
            </TableBody>
          </Table>
        </Scrollbar>
      </div>

      <TablePaginationCustom
        count={dataFiltered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        showDenseToggle={false}
        dense={dense}
        onChangeDense={onChangeDense}
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filterName,
  filterType,
  filterByTab
}: {
  inputData: (TaskGeneral | ListingOrderItemGeneral)[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterType: string;
  filterByTab: string;
}) {
  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  switch (filterByTab) {
    case 'Tasks':
      inputData = inputData.filter((el) => el instanceof TaskGeneral);
      break;
    case 'Work Orders':
      inputData = inputData.filter((el) => el instanceof ListingOrderItemGeneral);
      break;
    default:
      break;
  }

  if (filterType !== 'all') {
    inputData = inputData.filter((el) => {
      if (el instanceof TaskGeneral) {
        return el.type === filterType;
      }
      if (el instanceof ListingOrderItemGeneral) {
        return el.sub_type === filterType;
      }
      return false;
    });
  }
  if (filterName) {
    inputData = inputData.filter((el) => {
      if (el instanceof TaskGeneral) {
        return (
          el.title.toLowerCase().indexOf(filterName.toLowerCase()) > -1 ||
          (el.related_name &&
            el.related_name.toLowerCase().indexOf(filterName.toLowerCase()) > -1) ||
          el.unit_id.toLowerCase().indexOf(filterName.toLowerCase()) > -1
        );
      }
      if (el instanceof ListingOrderItemGeneral) {
        return (
          el.title.toLowerCase().indexOf(filterName.toLowerCase()) > -1 ||
          el.name_detailed.toLowerCase().indexOf(filterName.toLowerCase()) > -1
        );
      }
      return false;
    });
  }
  return inputData;
}
